import type { Photo } from '@/types/listing';

/**
 * Photo derivative selection — one source of truth for "which URL should THIS
 * surface ask for?".
 *
 * Listings arrive from two places and the difference is invisible in the type:
 *
 *  - **Processed** listings carry GCS derivatives — `thumb_url` (400×300, ~39 KB),
 *    `large_url` (1200×900, ~299 KB) and `url` = the original (1440×1080, ~409 KB),
 *    all `Cache-Control: max-age=2592000`.
 *  - **Freshly ingested** listings have no derivatives yet: only `url`, pointing at
 *    the raw MLSListings CDN (`media.mlslmedia.com`, `Cache-Control: no-cache`).
 *
 * Every surface used to fall back to `url`, so the newest homes — the ones on the
 * first screen of a "Newest" search — made the Next image optimizer fetch and
 * re-encode a full-size third-party original per request. The MLS CDN does expose a
 * second, much smaller variant: the path ends `/{sizeCode}/{index}` where **2** is
 * 1200×760 (~75–320 KB) and **1** is 200×127 (~4–10 KB). It is undocumented but
 * present on every photo checked (24/24 across 8 listings), and `mlsAtSize` degrades
 * to the original string if the shape ever changes, so nothing breaks if it goes away.
 */

/** Shipped SVG shown when a listing has no usable photo at all. */
export const PHOTO_PLACEHOLDER = '/placeholder-home.svg';

/** Pixel width of the `thumb` derivative — the widest box it fills without upscaling. */
export const THUMB_WIDTH = 400;

/**
 * An owned-media or legacy GCS derivative: `…/listings/{id}/{thumb|large}/{n}.jpg`.
 * `original` is deliberately excluded — it is only ever a last-resort fallback
 * for a listing whose derivatives do not exist yet, and there is nothing to
 * swap it for.
 */
const PROCESSED_DERIVATIVE =
  /^(https:\/\/(?:storage\.googleapis\.com\/[^/]+|media\.letsgoreal\.com)\/listings\/[^/]+)\/(?:thumb|large)\/(\d+\.jpg)$/;

/**
 * The cheapest derivative that can fill `width` without upscaling — i.e. the
 * cheapest thing the image optimizer should be asked to *download and decode*
 * in order to emit a `width`-wide frame. `null` when `src` is not ours.
 *
 * A result card asks for a 384 px frame, and until now the optimizer was handed
 * the 1200 × 799 / ~185 KB `large` to make it: 959k pixels decoded and resized
 * per image, ~18 times per search page. Handing it the 400 × 266 / ~26 KB
 * `thumb` instead produces a **byte-identical** frame (measured 16.0 KB vs
 * 16.3 KB at w=384, 8.4 KB vs 8.4 KB at w=256) from 9× fewer pixels.
 */
export function gcsOriginForWidth(src: string, width: number): string | null {
  const m = PROCESSED_DERIVATIVE.exec(src);
  if (!m) return null;
  return `${m[1]}/${width <= THUMB_WIDTH ? 'thumb' : 'large'}/${m[2]}`;
}

/**
 * How large the image will actually be painted, which decides how large a source
 * is worth fetching:
 *  - `micro` — filmstrip frames, compact-card thumbnails, map popovers (≤128 CSS px)
 *  - `card`  — result cards, similar-homes, compare, recently-viewed (≤640 CSS px)
 *  - `hero`  — detail gallery tiles and the full-screen lightbox
 */
export type PhotoSurface = 'micro' | 'card' | 'hero';

/**
 * `https://media.mlslmedia.com/property/MLSL/{mlsId}/{guid}/{sizeCode}/{index}`
 * Anchored on the media host so no other URL can be rewritten by accident.
 */
const MLS_MEDIA_URL = /^(https:\/\/media\.mlslmedia\.com\/property\/[^/]+\/[^/]+\/[^/]+)\/(\d+)\/(\d+)$/;

/** MLS CDN size codes. Only these two exist — 0 and 3+ all 404. */
const MLS_SIZE = { small: '1', full: '2' } as const;

/** Rewrite an MLS media URL to another size code. Any other URL passes through. */
function mlsAtSize(url: string, size: string): string {
  const m = MLS_MEDIA_URL.exec(url);
  return m ? `${m[1]}/${size}/${m[3]}` : url;
}

/** True when the URL is a raw MLS CDN original rather than one of our derivatives. */
export function isMlsMediaUrl(url: string | undefined): boolean {
  return !!url && MLS_MEDIA_URL.test(url);
}

/**
 * The cheapest source that still fills `surface` at full sharpness.
 *
 * Deliberately never returns the GCS **original**: it is 45% more bytes than
 * `large` for the same painted pixels at every width the web renders (measured),
 * so `large` is the ceiling and `url` is only a last-resort fallback.
 */
export function photoSrc(photo: Photo | undefined, surface: PhotoSurface): string {
  if (!photo?.url) return PHOTO_PLACEHOLDER;

  if (isMlsMediaUrl(photo.url)) {
    // No derivatives yet: pick between the CDN's own two sizes.
    return mlsAtSize(photo.url, surface === 'micro' ? MLS_SIZE.small : MLS_SIZE.full);
  }

  // `thumb` is only for boxes it can actually fill. It is **400 px wide**, so a
  // 356 px card on a 3× phone painted it into 1068 device pixels — measured, and
  // visibly mushy next to an unprocessed listing right beside it, which pulled a
  // 1080 px frame straight from the MLS CDN. Processed homes looked *worse* than
  // unprocessed ones. Cards therefore start from `large` and let the optimizer
  // emit exactly the width the browser asked for: a 1× desktop still only gets a
  // 384 px / ~26 KB frame, and only the screens that can resolve more pay more.
  if (surface === 'micro') return photo.thumb_url || photo.large_url || photo.url;
  return photo.large_url || photo.thumb_url || photo.url;
}

/**
 * A tiny, real frame of the same photo to paint *while* the full one decodes —
 * ~7 KB from the MLS CDN, ~39 KB from GCS. Returns `undefined` when the smallest
 * thing available is the image we are already loading, so callers can skip the
 * extra request instead of fetching the same bytes twice.
 */
export function photoPreviewSrc(photo: Photo | undefined, surface: PhotoSurface): string | undefined {
  if (!photo?.url || surface === 'micro') return undefined;
  const full = photoSrc(photo, surface);
  const preview = isMlsMediaUrl(photo.url)
    ? mlsAtSize(photo.url, MLS_SIZE.small)
    : photo.thumb_url;
  return preview && preview !== full ? preview : undefined;
}
